import React, {
  useState, useEffect, useMemo, useCallback, useRef,
} from 'react';
import { useSelector } from 'react-redux';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Card,
  Typography,
  Input,
  Modal,
  Progress,
  Alert,
  Tabs,
} from 'antd';
import {
  CloseCircleOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CheckCircleFilled,
  ExclamationCircleFilled,
  LoadingOutlined,
} from '@ant-design/icons';
import { onError, onSuccess } from 'utils/handlers';
import { useLocation } from 'react-router-dom';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import API from 'utils/api';
import { createDecimalRegExp } from 'utils/patterns';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import CurrencyInput from 'components/CurrencyInput';
import currencyFormater from 'utils/currencyFormatter';
import { isMutable } from 'utils/estadosGlobales';
import { selectEstadosGlobales } from 'store/catalogos';
import { URIconfiguracionesDeSegmentosPresupuestales } from 'utils/URIsSac';
import {
  CAMPO_POR_COLUMNA,
  getSegmentosDeConfiguracion,
  segmentoAplica,
} from 'utils/segmentosPresupuestales';
import { TIPO_PRESUPUESTO_EGRESO, ESTATUS_AUTORIZADO } from 'utils/constants';

export const permissionPresupuestoAnualNomina = {
  permissionModel: 'presupuestoanualdeegreso',
};

const { Text } = Typography;
const { TabPane } = Tabs;

const STATUS_TABS = [
  { key: 'activo', label: 'Activo', egId: 1 },
  { key: 'autorizado', label: 'Autorizado', egId: 4 },
];

const levelSorter = (a, b) => {
  if (a.nivel < b.nivel) return -1;
  if (a.nivel > b.nivel) return 1;
  return 0;
};

const mesNames = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const montosEn0 = {
  1: '0.00',
  2: '0.00',
  3: '0.00',
  4: '0.00',
  5: '0.00',
  6: '0.00',
  7: '0.00',
  8: '0.00',
  9: '0.00',
  10: '0.00',
  11: '0.00',
  12: '0.00',
};

// Estados posibles del proceso de sincronización
const SYNC_STATUS = {
  IDLE: 'idle',
  SYNCING: 'syncing',
  DONE: 'done',
  ERROR: 'error',
};

// Estados posibles del proceso de autorización (solo Ingresos soporta polling por ahora)
const AUTHORIZE_STATUS = {
  IDLE: 'idle',
  AUTHORIZING: 'authorizing',
  DONE: 'done',
  ERROR: 'error',
};

// eslint-disable-next-line react/prop-types
function PresupuestoAnualNomina({ permission }) {
  const egresosURI = 'configuraciones/presupuestos-anuales-de-egreso/';
  const egresosOptimizedURI = 'configuraciones/presupuestos-anuales-de-egreso-optimized/';
  const { pathname } = useLocation();
  const isEgresos = true;

  const baseURI = egresosURI;
  const gridURI = egresosOptimizedURI;

  const [meses, setMeses] = useState([]);
  const [tiposDePresupuesto, setTiposDePresupuesto] = useState([]);
  const [urs, setUrs] = useState([]);
  const estadosGlobales = useSelector(selectEstadosGlobales);
  const periodoFiscalSelected = useSelector(({ auth }) => auth.periodoFiscalSelected);

  const [decimal] = createDecimalRegExp(2);
  const [form] = Form.useForm();

  // --- Estado general ---
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [visibleConfirmAuthorize, setVisibleConfirmAuthorize] = useState(false);
  const [data, setData] = useState([]);
  const [showMeses, setShowMeses] = useState(true);
  const [disabled, setDisabled] = useState(true);
  const [tipoPresupuesto, setTipoPresupuesto] = useState(0);
  const [, setDetalladoMensual] = useState([]);
  const [asignationMensual, setAsignationMensual] = useState([]);
  const [mutable, setMutable] = useState(true);
  const [clases, setClases] = useState([]);
  const [showAsignaciones, setShowAsignaciones] = useState(false);
  const [cogDescripcion, setCogDescripcion] = useState('');
  const [directorioAreaDescripcion, setDirectorioAreaDescripcion] = useState('');
  const [conceptoDeIngresoDescripcion, setConceptoDeIngresoDescripcion] = useState('');
  const [statusTab, setStatusTab] = useState('activo');
  // undefined = cargando; null = sin configuración de segmentos (se muestran todas las columnas)
  const [segmentos, setSegmentos] = useState(undefined);
  const currentEgId = () => STATUS_TABS.find((t) => t.key === statusTab)?.egId;

  // --- Estado de sincronización (polling) ---
  const [visibleConfirmSync, setVisibleConfirmSync] = useState(false);
  const [visibleSyncProgress, setVisibleSyncProgress] = useState(false);
  const [syncStatus, setSyncStatus] = useState(SYNC_STATUS.IDLE);
  const [syncProgress, setSyncProgress] = useState({ total: 0, procesados: 0 });
  const [syncErrors, setSyncErrors] = useState([]);

  // Referencia al intervalo de polling activo para poder detenerlo al desmontar
  const pollingRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  // --- Estado de autorización (polling) ---
  const [visibleAuthorizeProgress, setVisibleAuthorizeProgress] = useState(false);
  const [authorizeStatus, setAuthorizeStatus] = useState(AUTHORIZE_STATUS.IDLE);
  const [authorizeProgress, setAuthorizeProgress] = useState({ total: 0, procesados: 0 });
  const [authorizeErrors, setAuthorizeErrors] = useState([]);
  const [authorizeErrorMessage, setAuthorizeErrorMessage] = useState('');

  // Referencia al intervalo de polling de autorización, independiente del de sincronización
  const authorizePollingRef = useRef(null);

  const stopAuthorizePolling = useCallback(() => {
    if (authorizePollingRef.current) {
      clearInterval(authorizePollingRef.current);
      authorizePollingRef.current = null;
    }
  }, []);

  // Detener los pollings al desmontar el componente
  useEffect(() => () => {
    stopPolling();
    stopAuthorizePolling();
  }, [stopPolling, stopAuthorizePolling]);

  // ─── Columnas memoizadas ────────────────────────────────────────────────────

  const columns = useMemo(() => {
    const aplica = (columna) => !CAMPO_POR_COLUMNA[columna]
      || segmentoAplica(segmentos, CAMPO_POR_COLUMNA[columna]);

    const segmentoCol = (titleText, dataIndex, width = 220) => ({
      titleText, dataIndex, key: dataIndex, width,
    });

    return [
      segmentoCol('Clave Presupuestal Completa', 'clave_presupuestal_completa', 280),
      segmentoCol('Clasificador por Objeto del Gasto', 'cog_descripcion'),
      segmentoCol('Área', 'directorio_de_area_descripcion', 200),
      segmentoCol('Destino Presupuestal', 'destino_presupuestal_descripcion'),
      segmentoCol('Programa', 'programa_descripcion'),
      segmentoCol('Proyecto', 'proyecto_descripcion'),
      segmentoCol('Función Presupuestal', 'funcion_presupuestal_descripcion'),
      segmentoCol('Clasificación Administrativa', 'clasificacion_administrativa_descripcion'),
      segmentoCol('Clasificación Funcional', 'clasificacion_funcional_descripcion'),
      segmentoCol('Clasificación Programática', 'clasificacion_programatica_descripcion'),
      segmentoCol('Tipo de Gasto', 'tipo_de_gasto_descripcion'),
      segmentoCol('Fuente de Financiamiento', 'fuente_financiamiento_descripcion'),
      segmentoCol('Tipo de Nómina Empleado', 'tipo_de_nomina_empleado_descripcion'),
      segmentoCol('Plan de Cuentas', 'cuenta_contable_conac_descripcion'),
      segmentoCol('Cuenta Contable', 'cuenta_contable_descripcion'),
    ].filter((col) => aplica(col.dataIndex)).concat([
      ...mesNames.map((nombre, idx) => ({
        titleText: nombre,
        dataIndex: `mes_${idx + 1}_importe`,
        key: `mes_${idx + 1}_importe`,
        align: 'right',
        render: (val) => currencyFormater(val || 0),
        exportRaw: true,
        exportFormat: '$#,##0.00',
        width: 130,
      })),
      {
        titleText: 'Monto Total',
        dataIndex: 'monto_total',
        key: 'monto_total',
        align: 'right',
        render: (val) => currencyFormater(val),
        exportRaw: true,
        exportFormat: '$#,##0.00',
        width: 160,
      },
    ]);
  }, [segmentos]);

  // ─── Reglas memoizadas ──────────────────────────────────────────────────────

  const rules = useMemo(() => ({
    required: [{ required: true, message: 'El campo es requerido' }],
    importe: [
      {
        validator: async (_, val) => {
          if (val && !decimal.test(val)) {
            throw new Error('Ingrese un valor decimal válido');
          }
        },
      },
    ],
    q_ur: [{ required: true, message: 'El campo es requerido' }],
    content_type: [{ required: true, message: 'El campo es requerido' }],
  }), [decimal]);

  // ─── rowSelection memoizado ─────────────────────────────────────────────────

  const rowSelection = useMemo(() => ({
    selectedRowKeys,
    type: 'radio',
  }), [selectedRowKeys]);

  // ─── fetchData estabilizado ─────────────────────────────────────────────────

  const fetchData = useCallback(async (egId) => {
    try {
      setLoading(true);
      const response = await API.get(gridURI, {
        params: { periodo_fiscal: periodoFiscalSelected.id, estados_globales: egId },
      });
      setData(response.data.map((e) => { delete e.periodo_fiscal; return e; }) || []);
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  }, [gridURI, periodoFiscalSelected]);

  // ─── useEffect inicial ──────────────────────────────────────────────────────

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        // Peticiones paralelas: catálogos + clases (ingresos) al mismo tiempo
        await Promise.all([
          !tiposDePresupuesto?.length
            ? API.get('/configuraciones/tipo-de-presupuestos-de-egresos/')
              .then((r) => setTiposDePresupuesto(r.data || []))
            : Promise.resolve(),
          !meses?.length
            ? API.get('/catalogos/meses/').then((r) => setMeses(r.data || []))
            : Promise.resolve(),
          !urs?.length
            ? API.get('/catalogos/content-types-ur/')
              .then((r) => setUrs([...(r.data || [])].sort(levelSorter)))
            : Promise.resolve(),
          !isEgresos
            ? API.get('configuraciones/clases/').then((r) => setClases(r.data))
            : Promise.resolve(),
        ]);
        await fetchData(STATUS_TABS[0].egId);
        try {
          const { data: configs } = await API.get(URIconfiguracionesDeSegmentosPresupuestales, {
            params: {
              estados_globales: ESTATUS_AUTORIZADO,
              tipo_de_presupuesto: TIPO_PRESUPUESTO_EGRESO,
            },
          });
          const [configuracion] = configs || [];
          setSegmentos(await getSegmentosDeConfiguracion(configuracion?.id));
        } catch (err) {
          setSegmentos(null);
        }
        setLoading(false);
      } catch (err) {
        onError(err, setLoading);
      }
    };
    fetchAll();
    // eslint-disable-next-line
  }, [periodoFiscalSelected, pathname]);

  // ─── Handlers del formulario ────────────────────────────────────────────────

  const onCancel = useCallback(() => {
    setVisible(false);
    setShowMeses(true);
    setDisabled(true);
    form.resetFields();
    setSelectedRowKeys([]);
    setTipoPresupuesto(0);
    setCogDescripcion('');
    setDirectorioAreaDescripcion('');
    setConceptoDeIngresoDescripcion('');
  }, [form]);

  const postAsignacionMensualIgual = useCallback(async ({ id, values }) => {
    try {
      const URI = `${baseURI}${id}/asignaciones-mensuales/`;
      const mappedPromises = asignationMensual
        .map((e, idx) => (e.mes === values[idx].catalogo_de_mes
          ? ({ ...e, importe: values[idx].importe }) : e))
        .sort((a, b) => a.importe - b.importe);
      const promises = mappedPromises.map((e, idx) => API.put(`${URI}${e.id}`, mappedPromises[idx]));
      const responses = promises.length ? await Promise.all(promises) : [];
      const datos = responses.map((res) => res.data);
      const asMen = Object.fromEntries(datos.map((e) => [e.mes, e.importe]));
      form.setFieldsValue(asMen);
    } catch (err) {
      onError(err, setLoading);
    }
  }, [baseURI, asignationMensual, form]);

  const onFinish = useCallback(async (_, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      values.saldo_por_asignar = values.saldo_por_asignar || 0;
      const asignacion_mensual = [];
      if (values.tipo_de_presupuesto !== 3) {
        if (!values.monto_mensual_igual) {
          values.monto_total = parseFloat(values.monto_total);
        }
        meses.forEach((mes) => {
          const catalogo_de_mes = mes.id;
          const importe = parseFloat(values[mes.id]) || 0;
          asignacion_mensual.push({ catalogo_de_mes, importe });
          delete values[mes.id];
        });
      }
      if (isEgresos) {
        values.importe = parseFloat(values.monto_total);
      }
      const [key] = selectedRowKeys;
      const response = await API.put(`${baseURI}${key}/`, values);
      if (response?.status === 200) {
        if ([2, 1].includes(response.data.tipo_de_presupuesto) || !isEgresos) {
          await postAsignacionMensualIgual({
            id: response.data.id,
            values: asignacion_mensual,
          });
        }
        onSuccess(response, 'Actualizado correctamente');
        await fetchData(currentEgId());
        if (!_continue) onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  }, [
    form, meses, isEgresos, selectedRowKeys, baseURI,
    postAsignacionMensualIgual, fetchData, onCancel,
  ]);

  const deleteItem = useCallback(async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const response = await API.delete(`${baseURI}${key}/`);
        if (response && response.status === 204) {
          onSuccess(response, 'Eliminado correctamente', 2.5);
          onCancel();
          await fetchData(currentEgId());
          setVisibleAlert(false);
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  }, [selectedRowKeys, baseURI, onCancel, fetchData]);

  const handleOnRowClick = useCallback((record) => {
    setTimeout(() => {
      form.resetFields();
      setSelectedRowKeys([record.id]);
      form.setFieldsValue(record);
      setMutable(isMutable({ estados_globales: record.estados_globales_id }));
      setCogDescripcion(record.cog_descripcion || '');
      setDirectorioAreaDescripcion(record.directorio_de_area_descripcion || '');
      setConceptoDeIngresoDescripcion(record.concepto_de_ingreso_descripcion || '');
    });
  }, [form]);

  const getUR = useCallback(async (values) => {
    try {
      setLoading(true);
      const { content_type, object_id } = values;
      if (!content_type || !object_id) {
        setLoading(false);
        return null;
      }
      const { nivel } = urs.find((e) => e.id === content_type) ?? {};
      const uris = {
        1: 'unidades-operativas',
        2: 'unidades-responsables',
        3: 'centros-de-costos',
      };
      const response = await API.get(`/estructura-organizacional/${uris[nivel]}/${object_id}/`);
      if (response?.status === 200 && response.data) {
        setLoading(false);
        return `${response.data.clave} - ${response.data.descripcion}`;
      }
    } catch (err) {
      onError(err, setLoading);
    }
    return null;
  }, [urs]);

  const getAsgnacionesMensuales = useCallback(async ({ id, presupuestoTipo }) => {
    try {
      setLoading(true);
      const response = await API.get(`${baseURI}${id}/asignaciones-mensuales/`);
      setAsignationMensual(response.data);
      if (presupuestoTipo === 3) {
        setDetalladoMensual(response.data);
      }
      setLoading(false);
      return response.data;
    } catch (err) {
      onError(err, setLoading);
      return [];
    }
  }, [baseURI]);

  const onClickEdit = useCallback(async () => {
    try {
      form.setFieldsValue({ monto_mensual_igual: undefined });
      const [key] = selectedRowKeys;
      const { data: match } = await API.get(`${baseURI}${key}/`);
      form.setFieldsValue(match);
      if (isEgresos) {
        const ur = await getUR(match);
        form.setFieldsValue({ ur });
        setTipoPresupuesto(match.tipo_de_presupuesto);
        setDisabled(true);
        setShowMeses(true);
      } else {
        setDisabled(match.monto_mensual_igual);
        if (match?.importe > 0) {
          form.setFieldsValue({ monto_mensual_igual: match.monto_mensual_igual });
        } else {
          form.setFieldsValue({ monto_mensual_igual: undefined });
        }
      }
      setShowAsignaciones(true);
      const asignacionesMensuales = await getAsgnacionesMensuales({ id: key });
      setShowAsignaciones(
        !asignacionesMensuales.every((e) => e.importe === 0)
          || [1, 2].includes(match?.tipo_de_presupuesto),
      );
      const asMen = Object.fromEntries(asignacionesMensuales.map((e) => [e.mes, e.importe]));
      form.setFieldsValue(asMen);
      if (match.tipo_de_presupuesto === 2) setDisabled(false);
      setVisible(true);
    } catch (err) {
      onError(err);
    }
  }, [form, selectedRowKeys, baseURI, isEgresos, getUR, getAsgnacionesMensuales]);

  const onClickDelete = useCallback(() => {
    setVisibleAlert(true);
  }, []);

  const asignacionMensual = useCallback(() => {
    const { monto_total, importe } = form.getFieldsValue();
    const montoTtl = monto_total || importe;
    const montoMensual = (montoTtl / 12).toFixed(2);
    setShowMeses(true);
    setDisabled(true);
    if (!Number.isNaN(montoMensual) && montoMensual !== 'NaN') {
      form.setFieldsValue({
        1: montoMensual,
        2: montoMensual,
        3: montoMensual,
        4: montoMensual,
        5: montoMensual,
        6: montoMensual,
        7: montoMensual,
        8: montoMensual,
        9: montoMensual,
        10: montoMensual,
        11: montoMensual,
        12: montoMensual,
        saldo_por_asignar: 0,
      });
      setShowAsignaciones(true);
    }
  }, [form]);

  const onChangeMonth = useCallback(() => {
    const values = form.getFieldsValue();
    const montoTtl = values?.monto_total || values?.importe;
    const totalAsignado = meses
      .map((e) => parseFloat(values[e.id]))
      .reduce((acum, curr) => acum + (parseFloat(curr) || 0), 0);
    form.setFieldsValue({
      saldo_por_asignar: (montoTtl - totalAsignado).toFixed(2) || 0,
    });
    form.validateFields(['saldo_por_asignar']);
  }, [form, meses]);

  const getLabel = useCallback(() => {
    const content_type = form.getFieldValue('content_type');
    const ur = urs.find((e) => e.id === content_type);
    return ur?.model;
  }, [form, urs]);

  const handleValuesChange = useCallback((changedValues, allValues) => {
    if (('monto_total' in changedValues && allValues.tipo_de_presupuesto === 1)
      || ('monto_mensual_igual' in changedValues && changedValues?.monto_mensual_igual)) {
      asignacionMensual();
    }
  }, [asignacionMensual]);

  // ─── Sincronización (polling) ───────────────────────────────────────────────

  const POLLING_INTERVAL_MS = 2000;

  /**
   * Inicia el polling al endpoint de estado de sincronización.
   * El backend responde: { status: 'running' | 'done', total, procesados, errors[] }
   */
  const startSyncPolling = useCallback((roomName) => {
    stopPolling();
    pollingRef.current = setInterval(async () => {
      try {
        const { data: progress } = await API.get(`${baseURI}estado-sincronizacion/`, {
          params: { room_name: roomName },
        });
        setSyncProgress({ total: progress.total, procesados: progress.procesados });
        if (progress.status === 'done') {
          stopPolling();
          setSyncErrors(progress.errors || []);
          setSyncStatus(SYNC_STATUS.DONE);
          fetchData(currentEgId()); // actualizar tabla silenciosamente
        }
      } catch (err) {
        stopPolling();
        setSyncStatus(SYNC_STATUS.ERROR);
        onError(err);
      }
    }, POLLING_INTERVAL_MS);
  }, [baseURI, fetchData, stopPolling]);

  const onConfirmSync = useCallback(async () => {
    stopPolling();

    const roomName = `sync-${isEgresos ? 'egresos' : 'ingresos'}-${Date.now()}`;

    // Resetear estado y mostrar modal de progreso
    setSyncProgress({ total: 0, procesados: 0 });
    setSyncErrors([]);
    setSyncStatus(SYNC_STATUS.SYNCING);
    setVisibleConfirmSync(false);
    setVisibleSyncProgress(true);

    try {
      await API.post(`${baseURI}sincronizar-cuentas-contables/`, {
        periodo_fiscal: periodoFiscalSelected?.id,
        room_name: roomName,
      });
      startSyncPolling(roomName);
    } catch (err) {
      setSyncStatus(SYNC_STATUS.ERROR);
      onError(err);
    }
  }, [baseURI, periodoFiscalSelected, isEgresos, startSyncPolling, stopPolling]);

  const onCloseSyncProgress = useCallback(() => {
    stopPolling();
    setVisibleSyncProgress(false);
    setSyncStatus(SYNC_STATUS.IDLE);
    setSyncProgress({ total: 0, procesados: 0 });
    setSyncErrors([]);
  }, [stopPolling]);

  // ─── Autorización ───────────────────────────────────────────────────────────

  /**
   * Inicia el polling al endpoint de estado de autorización (solo disponible para Ingresos).
   * El backend responde: { status: 'running' | 'done', total, procesados, errors[] }
   */
  const startAuthorizePolling = useCallback((roomName) => {
    stopAuthorizePolling();
    authorizePollingRef.current = setInterval(async () => {
      try {
        const { data: progress } = await API.get(`${baseURI}estado-autorizacion/`, {
          params: { room_name: roomName },
        });
        setAuthorizeProgress({ total: progress.total, procesados: progress.procesados });
        if (progress.status === 'done') {
          stopAuthorizePolling();
          setAuthorizeErrors(progress.errors || []);
          setAuthorizeStatus(AUTHORIZE_STATUS.DONE);
          if (progress.errors?.length) {
            // El proceso revirtió todo a Activo: no hay nada nuevo en Autorizado.
            fetchData(STATUS_TABS[0].egId);
          } else {
            setStatusTab('autorizado');
            fetchData(4);
          }
        }
      } catch (err) {
        stopAuthorizePolling();
        setAuthorizeStatus(AUTHORIZE_STATUS.ERROR);
        onError(err);
      }
    }, POLLING_INTERVAL_MS);
  }, [baseURI, fetchData, stopAuthorizePolling]);

  const onCloseAuthorizeProgress = useCallback(() => {
    stopAuthorizePolling();
    setVisibleAuthorizeProgress(false);
    setAuthorizeStatus(AUTHORIZE_STATUS.IDLE);
    setAuthorizeProgress({ total: 0, procesados: 0 });
    setAuthorizeErrors([]);
    setAuthorizeErrorMessage('');
  }, [stopAuthorizePolling]);

  const onConfirmAuthorize = useCallback(async () => {
    try {
      setLoading(true);
      {
        stopAuthorizePolling();
        const roomName = `authorize-${isEgresos ? 'egresos' : 'ingresos'}-${Date.now()}`;

        setAuthorizeProgress({ total: 0, procesados: 0 });
        setAuthorizeErrors([]);
        setAuthorizeErrorMessage('');
        setAuthorizeStatus(AUTHORIZE_STATUS.AUTHORIZING);
        setVisibleConfirmAuthorize(false);
        setVisibleAuthorizeProgress(true);

        await API.post(`${baseURI}autorizar/`, {
          periodo_fiscal: periodoFiscalSelected?.id,
          room_name: roomName,
        });
        startAuthorizePolling(roomName);
        setLoading(false);
      }
    } catch (err) {
      const { detail } = err?.response?.data || {};
      setAuthorizeErrorMessage(Array.isArray(detail) ? detail.join(', ') : detail || '');
      setAuthorizeStatus(AUTHORIZE_STATUS.ERROR);
      onError(err, setLoading);
    }
  }, [baseURI, periodoFiscalSelected, isEgresos, startAuthorizePolling, stopAuthorizePolling]);

  // ─── Derivados del estado de sincronización ─────────────────────────────────

  const syncPercent = syncProgress.total > 0
    ? Math.min(Math.round((syncProgress.procesados / syncProgress.total) * 100), 100)
    : 0;

  const syncIsDone = syncStatus === SYNC_STATUS.DONE;
  const syncIsError = syncStatus === SYNC_STATUS.ERROR;
  const syncIsRunning = syncStatus === SYNC_STATUS.SYNCING;

  const syncStatusIcon = () => {
    if (syncIsDone && syncErrors.length === 0) {
      return <CheckCircleFilled style={{ color: '#52c41a', fontSize: 20 }} />;
    }
    if (syncIsDone && syncErrors.length > 0) {
      return <ExclamationCircleFilled style={{ color: '#faad14', fontSize: 20 }} />;
    }
    if (syncIsError) {
      return <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />;
    }
    return <LoadingOutlined style={{ color: '#1677ff', fontSize: 20 }} spin />;
  };

  const syncStatusText = () => {
    if (syncStatus === SYNC_STATUS.SYNCING) {
      return syncProgress.total > 0
        ? `Procesados ${syncProgress.procesados} de ${syncProgress.total}`
        : 'Iniciando sincronización…';
    }
    if (syncIsDone && syncErrors.length === 0) return 'Sincronización completada exitosamente.';
    if (syncIsDone && syncErrors.length > 0) return `Sincronización completada con ${syncErrors.length} error(es).`;
    if (syncIsError) return 'Ocurrió un error. Verifique la conexión e intente nuevamente.';
    return '';
  };

  const syncProgressStatus = () => {
    if (syncIsError) return 'exception';
    if (syncIsDone) return 'success';
    return 'active';
  };

  // ─── Derivados del estado de autorización ──────────────────────────────────

  const authorizePercent = authorizeProgress.total > 0
    ? Math.min(Math.round((authorizeProgress.procesados / authorizeProgress.total) * 100), 100)
    : 0;

  const authorizeIsDone = authorizeStatus === AUTHORIZE_STATUS.DONE;
  const authorizeIsError = authorizeStatus === AUTHORIZE_STATUS.ERROR;
  const authorizeIsRunning = authorizeStatus === AUTHORIZE_STATUS.AUTHORIZING;

  const authorizeStatusIcon = () => {
    if (authorizeIsDone && authorizeErrors.length === 0) {
      return <CheckCircleFilled style={{ color: '#52c41a', fontSize: 20 }} />;
    }
    if (authorizeIsDone && authorizeErrors.length > 0) {
      return <ExclamationCircleFilled style={{ color: '#faad14', fontSize: 20 }} />;
    }
    if (authorizeIsError) {
      return <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />;
    }
    return <LoadingOutlined style={{ color: '#1677ff', fontSize: 20 }} spin />;
  };

  const authorizeStatusText = () => {
    if (authorizeStatus === AUTHORIZE_STATUS.AUTHORIZING) {
      return authorizeProgress.total > 0
        ? `Procesados ${authorizeProgress.procesados} de ${authorizeProgress.total}`
        : 'Iniciando autorización…';
    }
    if (authorizeIsDone && authorizeErrors.length === 0) return 'Autorización completada exitosamente.';
    if (authorizeIsDone && authorizeErrors.length > 0) {
      return `Autorización completada con ${authorizeErrors.length} error(es). Los presupuestos y la póliza `
        + 'fueron revertidos a "Activo" para que pueda corregir y reintentar.';
    }
    if (authorizeIsError) {
      return authorizeErrorMessage || 'Ocurrió un error. Verifique la conexión e intente nuevamente.';
    }
    return '';
  };

  const authorizeProgressStatus = () => {
    if (authorizeIsError || (authorizeIsDone && authorizeErrors.length > 0)) return 'exception';
    if (authorizeIsDone) return 'success';
    return 'active';
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <Row align="center" justify="center" className="container">
      <Col span={24}>
        <Spin tip="Cargando..." spinning={loading}>
          {!visible ? (
            <Tabs
              type="card"
              activeKey={statusTab}
              onChange={(key) => {
                setStatusTab(key);
                setSelectedRowKeys([]);
                const tab = STATUS_TABS.find((t) => t.key === key);
                if (tab) fetchData(tab.egId);
              }}
            >
              {STATUS_TABS.map(({ key, label }) => (
                <TabPane tab={label} key={key}>
                  <Table
                    cols={columns}
                    data={data}
                    permission={permission}
                    baseURI={baseURI}
                    rowSelection={rowSelection}
                    handleOnRowClick={handleOnRowClick}
                    controls={{ onClickEdit, onClickDelete }}
                    rowKey="id"
                    customActions={[
                      {
                        onClick: () => setVisibleConfirmSync(true),
                        text: 'Sincronizar',
                        icon: <SyncOutlined />,
                        disabledBySelection: false,
                        disabled: !(data.length && periodoFiscalSelected?.id),
                      },
                      {
                        onClick: () => setVisibleConfirmAuthorize(true),
                        text: 'Autorizar',
                        icon: <CheckCircleOutlined />,
                        disabledBySelection: false,
                        className: 'success',
                        disabled: !(data.length && periodoFiscalSelected?.id)
                          || data.every((e) => e.estados_globales_id === 4),
                      },
                    ]}
                  />
                </TabPane>
              ))}
            </Tabs>
          ) : (
            <Col span={24} style={{ height: '100%' }}>
              <Card
                className="form-container"
                title={(
                  <FormSubmitControls
                    label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Presupuesto Anual`}
                    onFinish={onFinish}
                    onCancel={onCancel}
                    mutable={mutable}
                  />
                )}
                bordered={false}
              >
                <Form
                  layout="vertical"
                  form={form}
                  onFinish={onFinish}
                  initialValues={{
                    estados_globales: 1,
                    periodo_fiscal: periodoFiscalSelected?.id,
                    saldo_por_asignar: 0,
                    monto_mensual_igual: undefined,
                    ...montosEn0,
                  }}
                  scrollToFirstError
                  onValuesChange={handleValuesChange}
                >
                  <Row gutter={10}>
                    {isEgresos ? (
                      // La UR solo existe en presupuestos ligados a una unidad (SAC); los de
                      // Nómina se generan desde la clave presupuestal y no la tienen.
                      <Form.Item
                        noStyle
                        shouldUpdate={(prev, cur) => prev.content_type !== cur.content_type}
                      >
                        {() => (getLabel() ? (
                          <Col xs={24} sm={24} md={8}>
                            <Form.Item name="ur" label={getLabel()}>
                              <Input disabled />
                            </Form.Item>
                          </Col>
                        ) : null)}
                      </Form.Item>
                    ) : (
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item name="clase_cri" label="Clasificador por Rubro de Ingresos">
                          <Select
                            dataSource={clases}
                            keyLabelRender
                            disabled={!mutable}
                            labelProp="denominacion"
                          />
                        </Form.Item>
                      </Col>
                    )}
                    <Form.Item hidden name="clave_presupuestal">
                      <Input disabled />
                    </Form.Item>
                    <Form.Item name="content_type" hidden>
                      <Input />
                    </Form.Item>
                    <Form.Item name="object_id" hidden>
                      <Input />
                    </Form.Item>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        label="Monto Total"
                        name={isEgresos ? 'monto_total' : 'importe'}
                        rules={rules.required}
                      >
                        <CurrencyInput style={{ width: '100%' }} disabled={!mutable} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item label="Saldo por Asignar" name="saldo_por_asignar">
                        <CurrencyInput style={{ width: '100%' }} disabled />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        label="Ejercicio"
                        rules={rules.required}
                        name="periodo_fiscal"
                      >
                        <Select
                          disabled={!mutable}
                          dataSource={[periodoFiscalSelected]}
                          render={(item) => item.periodo}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={8}>
                      {isEgresos ? (
                        <Form.Item
                          label="Tipo de Presupuesto"
                          rules={rules.required}
                          name="tipo_de_presupuesto"
                        >
                          <Select
                            // Sin Monto Detallado (tipo 3): es la captura por productos de SAC
                            dataSource={tiposDePresupuesto.filter((e) => [1, 2].includes(e.id))}
                            onChange={(val) => {
                              setTipoPresupuesto(val);
                              if (val === 1) {
                                asignacionMensual();
                              } else {
                                setShowMeses(true);
                                setDisabled(false);
                                form.setFieldsValue(montosEn0);
                              }
                              if (val === 3) {
                                getAsgnacionesMensuales({
                                  id: selectedRowKeys[0],
                                  presupuestoTipo: val,
                                });
                              }
                              onChangeMonth();
                            }}
                            disabled={!mutable}
                          />
                        </Form.Item>
                      ) : (
                        <Form.Item
                          name="monto_mensual_igual"
                          label="¿Es Monto Mensual Igual?"
                        >
                          <Select
                            disabled={!mutable}
                            trueFalse
                            onChange={(val) => {
                              if (val) {
                                asignacionMensual();
                              } else {
                                setDisabled(false);
                                form.setFieldsValue(montosEn0);
                              }
                            }}
                          />
                        </Form.Item>
                      )}
                    </Col>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="estados_globales"
                        label="Estado"
                        rules={rules.required}
                        hasFeedback
                      >
                        <Select disabled={!mutable} dataSource={estadosGlobales} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                      <Form.Item name="clave_presupuestal_completa" label="Clave Presupuestal">
                        <Input disabled />
                      </Form.Item>
                    </Col>
                    {isEgresos && (
                      <Col xs={24} sm={12} md={6}>
                        <Form.Item label="Clasificador por Objeto del Gasto">
                          <Input disabled value={cogDescripcion} />
                        </Form.Item>
                      </Col>
                    )}
                    {!isEgresos && (
                      <Col xs={24} sm={12} md={6}>
                        <Form.Item label="Concepto de Ingreso">
                          <Input disabled value={conceptoDeIngresoDescripcion} />
                        </Form.Item>
                      </Col>
                    )}
                    {segmentoAplica(segmentos, 'directorio_de_area') && (
                      <Col xs={24} sm={12} md={6}>
                        <Form.Item label="Directorio de Área">
                          <Input disabled value={directorioAreaDescripcion} />
                        </Form.Item>
                      </Col>
                    )}
                    {isEgresos && (
                      <>
                        <Col xs={24} sm={24} md={12} lg={8}>
                          <Form.Item name="cuenta_cargo" label="Cuenta de Cargo">
                            <Input disabled />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={8}>
                          <Form.Item name="cuenta_abono" label="Cuenta de Abono">
                            <Input disabled />
                          </Form.Item>
                        </Col>
                      </>
                    )}
                    {!isEgresos && (
                      <>
                        <Col xs={24} sm={24} md={12} lg={12}>
                          <Form.Item name="cuenta_de_recaudado" label="Cuenta de Recaudado">
                            <Input disabled={!mutable} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12}>
                          <Form.Item name="cuenta_devengado" label="Cuenta Devengado">
                            <Input disabled={!mutable} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12}>
                          <Form.Item name="cuenta_estimado" label="Cuenta Estimado">
                            <Input disabled={!mutable} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12}>
                          <Form.Item name="cuenta_por_ejecutar" label="Cuenta por Ejecutar">
                            <Input disabled={!mutable} />
                          </Form.Item>
                        </Col>
                      </>
                    )}
                  </Row>
                  {((tipoPresupuesto && tipoPresupuesto !== 3) || showAsignaciones) && (
                    <Row gutter={10}>
                      {meses.map((mes) => showMeses && (
                        <Col xs={24} sm={24} md={6} key={mes.id}>
                          <Form.Item
                            label={mes.nombre}
                            name={`${mes.id}`}
                            rules={rules.importe}
                          >
                            <CurrencyInput
                              style={{ width: '100%' }}
                              disabled={disabled}
                              onChange={onChangeMonth}
                            />
                          </Form.Item>
                        </Col>
                      ))}
                    </Row>
                  )}
                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Form>
              </Card>
            </Col>
          )}

          <ModalDelete
            onDelete={deleteItem}
            onCancel={() => setVisibleAlert(false)}
            open={visibleAlert}
            content={(
              <Text style={{ fontSize: 16 }}>
                ¿Desea eliminar el Presupuesto Anual de Nómina
                {' '}
                {data.find((e) => e.id === selectedRowKeys[0])?.clave_presupuestal_visible}
                ?
              </Text>
            )}
          />
        </Spin>
      </Col>

      {/* ── Modal de confirmación de sincronización ──────────────────────── */}
      <Modal
        open={visibleConfirmSync}
        title={(
          <Row justify="space-between" align="middle">
            <span>Confirmar sincronización</span>
            <FormSubmitControls
              onFinish={onConfirmSync}
              onCancel={() => setVisibleConfirmSync(false)}
              onFinishText="Sincronizar"
              onFinishIcon={<SyncOutlined />}
            />
          </Row>
        )}
        footer={null}
        closable={false}
      >
        <Row justify="center" style={{ flex: 1 }}>
          <Text style={{ textAlign: 'center' }}>
            ¿Seguro que desea iniciar el proceso de sincronización
            <br />
            de Cuentas Contables correspondientes al Periodo Fiscal
            <br />
            <strong>
              {periodoFiscalSelected?.periodo}
              {periodoFiscalSelected?.identificador ? ` - ${periodoFiscalSelected.identificador}` : ''}
            </strong>
            ?
          </Text>
        </Row>
      </Modal>

      {/* ── Modal de progreso en tiempo real (polling) ────────────────────── */}
      <Modal
        open={visibleSyncProgress}
        closable={false}
        maskClosable={false}
        footer={null}
        title={(
          <Row align="middle" gap={8} style={{ gap: 10 }}>
            {syncStatusIcon()}
            <span style={{ marginLeft: 8 }}>
              {syncIsRunning && 'Sincronizando cuentas contables…'}
              {syncIsDone && syncErrors.length === 0 && 'Sincronización completada'}
              {syncIsDone && syncErrors.length > 0 && 'Sincronización completada con errores'}
              {syncIsError && 'Error en la sincronización'}
            </span>
          </Row>
        )}
        width={520}
      >
        <Row style={{ flexDirection: 'column', gap: 16 }}>
          {/* Barra de progreso */}
          <Progress
            percent={syncIsDone || syncIsError ? 100 : syncPercent}
            status={syncProgressStatus()}
            strokeColor={syncIsError ? '#ff4d4f' : undefined}
          />

          {/* Texto de estado */}
          <Text
            type={syncIsError ? 'danger' : undefined}
            style={{ textAlign: 'center', display: 'block' }}
          >
            {syncStatusText()}
          </Text>

          {/* Lista de errores parciales (mensajes 'error' del WS) */}
          {syncErrors.length > 0 && (
            <Alert
              type="warning"
              message={`Se encontraron ${syncErrors.length} error(es) durante la sincronización:`}
              description={(
                <ul
                  style={{
                    margin: '8px 0 0 0',
                    paddingLeft: 20,
                    maxHeight: 200,
                    overflowY: 'auto',
                  }}
                >
                  {syncErrors.map((err, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <li key={i} style={{ marginBottom: 4 }}>{err}</li>
                  ))}
                </ul>
              )}
              showIcon
            />
          )}

          {/* Botón de cerrar (solo disponible al terminar) */}
          <Row justify="end">
            <Button
              type={syncErrors.length > 0 ? 'default' : 'primary'}
              onClick={onCloseSyncProgress}
              disabled={syncIsRunning}
              icon={syncIsRunning ? <LoadingOutlined /> : <CheckCircleOutlined />}
            >
              {syncIsRunning ? 'Sincronizando…' : 'Cerrar'}
            </Button>
          </Row>
        </Row>
      </Modal>

      {/* ── Modal de autorización ────────────────────────────────────────── */}
      <Modal
        open={visibleConfirmAuthorize}
        title={(
          <Row justify="space-between">
            Confirmar Autorización
            <FormSubmitControls
              onFinish={onConfirmAuthorize}
              onCancel={() => setVisibleConfirmAuthorize(false)}
              onFinishText="Autorizar"
              onFinishIcon={<CheckCircleOutlined />}
            />
          </Row>
        )}
        footer={null}
        closable={false}
      >
        <Row justify="center" style={{ flex: 1 }}>
          <Text style={{ textAlign: 'center' }}>
            ¿Seguro que desea iniciar el proceso de autorización
            <br />
            de Cuentas Contables correspondientes al Periodo Fiscal
            <br />
            {periodoFiscalSelected?.periodo}
            {periodoFiscalSelected?.identificador ? ` - ${periodoFiscalSelected.identificador}` : ''}
            ?
          </Text>
        </Row>
      </Modal>

      {/* ── Modal de progreso de autorización en tiempo real (polling) ─────── */}
      <Modal
        open={visibleAuthorizeProgress}
        closable={false}
        maskClosable={false}
        footer={null}
        title={(
          <Row align="middle" gap={8} style={{ gap: 10 }}>
            {authorizeStatusIcon()}
            <span style={{ marginLeft: 8 }}>
              {authorizeIsRunning && `Autorizando presupuesto anual de ${isEgresos ? 'egresos' : 'ingresos'}…`}
              {authorizeIsDone && authorizeErrors.length === 0 && 'Autorización completada'}
              {authorizeIsDone && authorizeErrors.length > 0 && 'Autorización completada con errores'}
              {authorizeIsError && 'Error en la autorización'}
            </span>
          </Row>
        )}
        width={520}
      >
        <Row style={{ flexDirection: 'column', gap: 16 }}>
          <Progress
            percent={authorizeIsDone || authorizeIsError ? 100 : authorizePercent}
            status={authorizeProgressStatus()}
            strokeColor={authorizeIsError || (authorizeIsDone && authorizeErrors.length > 0) ? '#ff4d4f' : undefined}
          />

          <Text
            type={(authorizeIsError || (authorizeIsDone && authorizeErrors.length > 0)) ? 'danger' : undefined}
            style={{ textAlign: 'center', display: 'block' }}
          >
            {authorizeStatusText()}
          </Text>

          {authorizeErrors.length > 0 && (
            <Alert
              type="warning"
              message={`Se encontraron ${authorizeErrors.length} error(es) durante la autorización:`}
              description={(
                <ul
                  style={{
                    margin: '8px 0 0 0',
                    paddingLeft: 20,
                    maxHeight: 200,
                    overflowY: 'auto',
                  }}
                >
                  {authorizeErrors.map((err, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <li key={i} style={{ marginBottom: 4 }}>{err}</li>
                  ))}
                </ul>
              )}
              showIcon
            />
          )}

          <Row justify="end">
            <Button
              type={authorizeErrors.length > 0 ? 'default' : 'primary'}
              onClick={onCloseAuthorizeProgress}
              disabled={authorizeIsRunning}
              icon={authorizeIsRunning ? <LoadingOutlined /> : <CheckCircleOutlined />}
            >
              {authorizeIsRunning ? 'Autorizando…' : 'Cerrar'}
            </Button>
          </Row>
        </Row>
      </Modal>
    </Row>
  );
}

export default PresupuestoAnualNomina;
